/**
 * 2026 縣市長選舉開票中心 — 開發用假資料
 *
 * ⚠️ 全部為虛構示範資料，候選人姓名、票數、開票率皆非真實。
 *
 * 用途：後端 API 尚未就緒前的開發與驗收。輸出格式完全比照
 * docs/election2026/API_CONTRACT.md，屆時只需將 service 層的取數來源
 * 換成實際的 CDN endpoint，元件與頁面不需改動。
 *
 * 刻意放在 utils/ 而非 static/ —— static/ 目錄會直接對外公開，
 * 假票數被搜尋引擎索引會造成困擾。
 */

import { COUNTIES } from './constants'
import { buildLastElection2022 } from './lastElection2022'

/**
 * 假候選人姓名池
 */
const NAMES = [
  '林俊宏', '陳雅萍', '黃志偉', '張淑芬', '李建成', '王美玲',
  '吳宗翰', '劉佩君', '蔡明彥', '楊惠如', '許文彬', '鄭雅雯',
  '謝東霖', '洪淑娟', '曾國強', '賴姿吟', '周孟儒', '徐千惠',
  '簡上仁', '呂佳蓉', '高志明', '范文君', '彭建良', '施慧珊',
  '柯建志', '邱雅琪', '孫振宇', '馮曉薇', '董俊傑', '葉宜靜',
  '沈柏翰', '傅嘉玲', '溫世豪', '康雅筑', '石志豪', '莊淑惠',
  '潘仲庭', '游雅筑', '鍾政宏', '田佳蓉', '涂志遠', '侯美慧',
  '藍威廷', '闕曉萍', '巫俊霖', '樊心怡'
]

/**
 * 各縣市的假開票情境
 *
 * 欄位：[slug, 領先黨, 領先得票率, 第二黨, 第二得票率, 開票率, 應開票所, 有效票, 強制狀態]
 *
 * 情境刻意涵蓋 SPEC §5 的所有卡片變體：
 * - 兩人對決·開票中（多數縣市）
 * - 兩人對決·已當選（台中、金門、連江）
 * - 三腳督（新竹市）
 * - 四人以上（台北市）
 * - 開票率 0%（高雄市）
 * - 自行宣布當選（新北市）
 * - 開票率 < 30% 的斜線紋理（雲林、苗栗、澎湖等）
 */
const SCENARIOS = [
  ['taipei', 'kmt', 41.8, 'dpp', 39.0, 65, 1664, 963000, null],
  ['newtaipei', 'kmt', 50.9, 'dpp', 46.9, 88, 1821, 1156000, 'self_declared'],
  ['taoyuan', 'kmt', 51.2, 'dpp', 48.8, 42, 1755, 250885, null],
  ['taichung', 'dpp', 53.6, 'kmt', 46.4, 100, 1523, 843522, null],
  ['tainan', 'dpp', 57.4, 'kmt', 42.6, 92, 1338, 712000, null],
  ['kaohsiung', 'dpp', 0, 'kmt', 0, 0, 1622, 0, null],
  ['keelung', 'dpp', 49.6, 'kmt', 47.1, 71, 297, 148000, null],
  ['hsinchucity', 'tpp', 38.4, 'kmt', 34.6, 78, 302, 161000, null],
  ['hsinchucounty', 'kmt', 46.2, 'dpp', 41.3, 24, 362, 183000, null],
  ['miaoli', 'kmt', 55.1, 'dpp', 44.9, 18, 462, 201000, null],
  ['changhua', 'kmt', 48.3, 'dpp', 47.2, 57, 861, 432000, null],
  ['nantou', 'kmt', 52.7, 'dpp', 47.3, 83, 481, 197000, null],
  ['yunlin', 'dpp', 50.4, 'kmt', 49.6, 12, 522, 236000, null],
  ['chiayicity', 'dpp', 54.8, 'kmt', 45.2, 96, 151, 109000, null],
  ['chiayicounty', 'dpp', 58.2, 'kmt', 41.8, 61, 388, 181000, null],
  ['pingtung', 'dpp', 52.1, 'kmt', 47.9, 45, 701, 309000, null],
  ['yilan', 'dpp', 49.9, 'kmt', 48.4, 33, 331, 175000, null],
  ['hualien', 'kmt', 61.3, 'dpp', 38.7, 74, 240, 124000, null],
  ['taitung', 'kmt', 59.7, 'dpp', 40.3, 27, 204, 91000, null],
  ['penghu', 'independent', 44.5, 'dpp', 41.2, 8, 187, 44000, null],
  ['kinmen', 'independent', 40.1, 'kmt', 37.6, 100, 98, 58000, null],
  ['lienchiang', 'kmt', 63.0, 'independent', 37.0, 100, 29, 7000, null]
]

const PARTY_NAMES = {
  kmt: '中國國民黨',
  dpp: '民主進步黨',
  tpp: '台灣民眾黨',
  independent: '無黨籍'
}

/**
 * 額外的小額得票候選人數
 *
 * 實際選舉中一個選區的登記參選人可能達數十位，多數得票率不到 1%。
 * 此處刻意為幾個縣市加料，用來驗證卡片的得票率門檻與「其他 N 位」收合：
 *
 * - taipei：四人以上，第三名過門檻、第四名未過
 * - changhua：多位小額參選人，全部應收合
 * - kaohsiung：開票率 0%，用來驗證開票前的呈現
 */
const EXTRA_SMALL_CANDIDATES = {
  taipei: 1,
  changhua: 5,
  kaohsiung: 4
}

/**
 * 依 slug 取得縣市基本資料
 */
function findCounty(slug) {
  return COUNTIES.filter(county => county.slug === slug)[0]
}

/**
 * 產生單一縣市的假資料，輸出格式比照契約 §3.3
 *
 * @param {array} scenario SCENARIOS 的一筆
 * @param {number} index 用於決定姓名與資料來源的變化
 * @param {string} timestamp 產出時間（ISO 8601）
 */
function buildCounty(scenario, index, timestamp) {
  const slug = scenario[0]
  const leadParty = scenario[1]
  const leadRate = scenario[2]
  const secondParty = scenario[3]
  const secondRate = scenario[4]
  const countedRate = scenario[5]
  const totalStations = scenario[6]
  const validVotes = scenario[7]
  const forcedStatus = scenario[8]

  const county = findCounty(slug)
  const notStarted = countedRate === 0

  // status 推導規則見契約 §4.2。self_declared 為人工旗標，不可由數字推導
  let status
  if (forcedStatus) {
    status = forcedStatus
  } else if (notStarted) {
    status = 'not_started'
  } else if (countedRate >= 100) {
    status = 'confirmed'
  } else {
    status = 'counting'
  }

  const elected = status === 'confirmed'
  let nameCursor = index * 2

  const candidates = [
    {
      number: 1,
      name: NAMES[nameCursor++ % NAMES.length],
      party: leadParty,
      partyName: PARTY_NAMES[leadParty],
      votes: Math.round(validVotes * leadRate / 100),
      rate: leadRate,
      elected
    },
    {
      number: 2,
      name: NAMES[nameCursor++ % NAMES.length],
      party: secondParty,
      partyName: PARTY_NAMES[secondParty],
      votes: Math.round(validVotes * secondRate / 100),
      rate: secondRate,
      elected: false
    }
  ]

  const extraCount = EXTRA_SMALL_CANDIDATES[slug] || 0

  if (notStarted) {
    // 開票前沒有票數，仍需列出完整候選人名單。
    // 第一位給民眾黨（三大黨，開票前應列在卡片上），其餘為無黨籍（應收合）
    for (let i = 0; i < extraCount; i++) {
      const party = i === 0 ? 'tpp' : 'independent'
      candidates.push({
        number: candidates.length + 1,
        name: NAMES[nameCursor++ % NAMES.length],
        party,
        partyName: PARTY_NAMES[party],
        votes: 0,
        rate: 0,
        elected: false
      })
    }
  } else {
    // 剩餘票數分配給第三位以後的候選人，遞減分配
    const remainder = Number((100 - leadRate - secondRate).toFixed(1))

    if (remainder >= 1.5) {
      const smallCount = extraCount + 1
      let left = remainder

      for (let i = 0; i < smallCount; i++) {
        const isLast = i === smallCount - 1
        // 第三名拿走大部分，其餘遞減，模擬真實的長尾分布
        const rate = isLast ? Number(left.toFixed(1)) : Number((left * 0.62).toFixed(1))
        left = Number((left - rate).toFixed(1))

        const party = i === 0
          ? ((leadParty === 'tpp' || secondParty === 'tpp') ? 'independent' : 'tpp')
          : 'independent'

        candidates.push({
          number: candidates.length + 1,
          name: NAMES[nameCursor++ % NAMES.length],
          party,
          partyName: PARTY_NAMES[party],
          votes: Math.round(validVotes * rate / 100),
          rate,
          elected: false
        })

        if (left <= 0) {
          break
        }
      }
    }
  }

  const countedStations = Math.round(totalStations * countedRate / 100)

  // 逐縣市給不同的 source，驗證「卡片必須逐一標示來源」
  let source = 'cec'
  if (index % 7 === 3) {
    source = 'live'
  } else if (index % 11 === 5) {
    source = 'manual'
  }

  return {
    slug,
    name: county.name,
    shortName: county.shortName,
    cecCode: county.cecCode,
    status,
    source,
    pollingStations: { counted: countedStations, total: totalStations },
    countedRate,
    validVotes,
    invalidVotes: notStarted ? 0 : Math.round(validVotes * 0.006),
    turnoutRate: notStarted ? null : Number((58 + (index % 9) * 1.3).toFixed(1)),
    candidates,
    leadGap: notStarted
      ? null
      : {
          votes: candidates[0].votes - candidates[1].votes,
          rate: Number((leadRate - secondRate).toFixed(1))
        },
    updatedAt: timestamp
  }
}

/**
 * 將縣市轉為「尚未開票」狀態
 *
 * 投票日下午 4 點才開始開票，在那之前 22 縣市全部是 0%：
 * 候選人名單已定但票數全為 0，卡片改以三大黨呈現、其餘收合。
 * 預熱期（phase === 'preheat'）也是同一種畫面。
 *
 * @param {object} county buildCounty 產出的縣市
 * @returns {object} 票數歸零後的縣市
 */
function toNotStarted(county) {
  return Object.assign({}, county, {
    status: 'not_started',
    countedRate: 0,
    pollingStations: { counted: 0, total: county.pollingStations.total },
    validVotes: 0,
    invalidVotes: 0,
    turnoutRate: null,
    leadGap: null,
    candidates: county.candidates.map(candidate => Object.assign({}, candidate, {
      votes: 0,
      rate: 0,
      elected: false
    }))
  })
}

/**
 * 產生完整的 results.json 假資料
 *
 * @param {object} options
 * @param {string} options.phase 覆寫 phase，預設 'live'
 * @returns {object} 比照契約 §3 的完整 payload
 */
export function buildMockResults(options) {
  const settings = options || {}
  const phase = settings.phase || 'live'
  const timestamp = new Date().toISOString()

  let counties = SCENARIOS.map((scenario, index) => buildCounty(scenario, index, timestamp))

  // 預熱期：全部縣市尚未開票
  if (phase === 'preheat') {
    counties = counties.map(toNotStarted)
  }

  const countedStations = counties.reduce((sum, county) => sum + county.pollingStations.counted, 0)
  const totalStations = counties.reduce((sum, county) => sum + county.pollingStations.total, 0)

  // 席次統計：由縣市資料推導，正式環境由後端提供
  const tally = {}
  counties.forEach((county) => {
    if (county.status === 'not_started') {
      return
    }
    const leader = county.candidates[0]
    if (!tally[leader.party]) {
      tally[leader.party] = { party: leader.party, elected: 0, leading: 0 }
    }
    if (county.status === 'confirmed') {
      tally[leader.party].elected += 1
    } else {
      tally[leader.party].leading += 1
    }
  })

  const isPreheat = phase === 'preheat'

  return {
    phase,
    updatedAt: timestamp,
    national: {
      // 開票前尚無投票率可言
      turnoutRate: isPreheat ? null : 68.4,
      countedRate: Number((countedStations / totalStations * 100).toFixed(1)),
      pollingStations: { counted: countedStations, total: totalStations },
      seats: ['kmt', 'dpp', 'tpp', 'independent'].map(party => tally[party]).filter(Boolean)
    },
    counties
  }
}

/**
 * 2022 上屆結果
 *
 * 這一份不是 mock —— 2022 選舉是既成事實，直接取用真實資料。
 * 見 utils/election2026/lastElection2022.js（票數待從中選會補齊）。
 *
 * @returns {object} 比照契約 §8 的完整 payload
 */
export function buildMockLastElection() {
  return buildLastElection2022(COUNTIES)
}
